import type { PageBlock, SitePage } from "@/lib/content/types";
import { FAQS, HERO_SLIDES, PROMO_CARDS, STUDIOS, USPS } from "@/lib/data";
import { ABOUT_DOCS, BLOG_POSTS, LEGAL_DOCS, SUPPORT_DOCS, type Doc } from "@/lib/site";
import { BUILTIN_PAGES, CATEGORY_GAMES, CATEGORY_PILLS, LOBBY_BROWSER, type BuiltinPageId } from "./blocks";

/* How each shipped page is composed, in the order the kit draws it — the
   homepage its sections one by one, the casino lobby a small hero banner and
   the kit's lobby, the category page that banner over the category pills and
   the category's games, the other pages a header and their body — and, since every content
   block owns its copy per instance, what each block on each page says by
   default. Editing any of this in /admin → Page builder
   replaces the whole list for the site; the ids stay stable so a brand
   snapshot and a later default still refer to the same page. */

const block = (id: string, type: string, props: Record<string, unknown> = {}): PageBlock => ({ id, type, props });

const header = (id: BuiltinPageId, title: string, body: string): PageBlock =>
  block(`${id}-header`, "pageHeader", { title, body, image: "" });

/* The long-form documents are pages too: one page per document, one document
   block each, addressed under the group it belongs to. The footer's links
   point at these addresses; a page an operator deletes simply 404s. */
const docPages = (group: string, docs: Record<string, Doc>): SitePage[] =>
  Object.entries(docs).map(([slug, doc]) => ({
    id: `${group}-${slug}`,
    slug: `${group}/${slug}`,
    title: doc.title,
    description: doc.intro,
    blocks: [
      block(`${group}-${slug}-doc`, "document", {
        title: doc.title,
        intro: doc.intro,
        sections: doc.sections.map((section) => ({ ...section })),
      }),
    ],
  }));

const page = (id: BuiltinPageId, blocks: PageBlock[]): SitePage => ({
  id,
  slug: BUILTIN_PAGES[id].slug,
  title: BUILTIN_PAGES[id].title,
  description: "",
  blocks,
});

export const DEFAULT_PAGES: SitePage[] = [
  page("home", [
    block("home-hero", "hero", { copy: true, slides: HERO_SLIDES.map((s) => ({ ...s })) }),
    block("home-usps", "usps", { items: USPS.map((u) => ({ ...u })) }),
    block("home-live-wins-strip", "liveWinsStrip"),
    block("home-promo-carousel", "promoCarousel", { cards: PROMO_CARDS.map((c) => ({ ...c })) }),
    block("home-big-wins", "bigWins"),
    block("home-bankroll", "bankroll"),
    block("home-live-wins-ticker", "liveWinsTicker"),
    block("home-game-browser", "gameBrowser", {
      rows: [],
      categoryRows: 3,
      gamesPerRow: 14,
      searchBar: "Below the pills",
      categoryCards: false,
      sections: "Panels",
      tiles: "Standard",
    }),
    block("home-studio-strip", "studioStrip"),
    block("home-live-stats", "liveStats"),
    block("home-content-block", "contentBlock", {
      eyebrow: "Why Flipcoin",
      headline: "Casino, without the small print.",
      body: "We've been gambling online for years and got tired of what everyone else did: confusing bonuses with 40x wagering requirements, withdrawals stuck in “processing” for days, KYC forms that made you feel like a suspect just for wanting to cash out, and chatbots whose only job was to stall, deflecting, delaying, doing anything but actually paying you out. We accepted it because we thought that was just how it worked.",
      kicker: "It's not.",
      stats: [
        { k: "$0", v: "Withdrawal fees" },
        { k: "0x", v: "Wagering on cashback" },
        { k: "24/7", v: "Live chat with people" },
      ],
    }),
    block("home-faq", "faq", { items: FAQS.map((f) => ({ ...f })) }),
  ]),
  page("casino", [
    block("casino-hero", "hero", { size: "Small", copy: false, slides: HERO_SLIDES.map((s) => ({ ...s })) }),
    block("casino-game-browser", "gameBrowser", { ...LOBBY_BROWSER }),
  ]),
  page("category", [
    block("category-hero", "hero", { size: "Small", copy: false, slides: HERO_SLIDES.map((s) => ({ ...s })) }),
    block("category-pills", "categoryPills", { ...CATEGORY_PILLS }),
    block("category-games", "categoryGames", { ...CATEGORY_GAMES }),
  ]),
  page("promotions", [
    header(
      "promotions",
      "Promotions",
      "Races, cashback, free spins and missions. Everything running right now, and what you get for taking part.",
    ),
    block("promotions-list", "promotionsList"),
  ]),
  page("leaderboards", [
    block("leaderboards-body", "leaderboards", {
      title: "Leaderboards",
      body: "Every race we are running, with live standings and wagering volume. Boards update as rounds settle.",
    }),
  ]),
  page("rewards", [
    header("rewards", "Rewards", "Cashback, rakeback and every bonus running right now. Sign in to see what you have earned."),
    block("rewards-body", "rewards"),
  ]),
  page("studios", [
    header(
      "studios",
      "Studios",
      "Every studio we integrate, and the games they build. Each one is certified independently before it reaches the lobby.",
    ),
    block("studios-grid", "studiosGrid", { curated: [...STUDIOS] }),
  ]),
  page("blog", [
    header("blog", "Blog", "Guides, product notes and the occasional explanation of why we did something the hard way."),
    block("blog-list", "blogList"),
  ]),
  page("missions", [
    header(
      "missions",
      "Missions",
      "What each mission asks for is set here. How far into it you are is counted from your own play on the platform.",
    ),
    block("missions-board", "missionBoard"),
  ]),
  ...docPages("legal", LEGAL_DOCS),
  ...docPages("support", SUPPORT_DOCS),
  ...docPages("about", ABOUT_DOCS),
  ...docPages("blog", BLOG_POSTS),
];
